import React, { memo } from 'react';
import PropTypes from 'prop-types';
import NumberFormat from 'react-number-format';
// import dayjs from 'dayjs';
import { useSelector } from 'react-redux';
import InfoDropdownPopover from '../../../../InfoDropdownPopover';
import currencySymbol from '../../../../../helpers/currencySymbol';
import getHotelRate from '../../../../../helpers/selectors/getHotelRate';
import { Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { RiInformationLine } from 'react-icons/ri';
import DisplayCurrencyMessage from '../DisplayCurrencyMessage/index';
import { HotelRate, RootState, User } from '../../../../../types/hotel';

const LightTooltip = withStyles((theme) => ({
  tooltip: {
    fontFamily: 'var(--fontFamilyBody)',
    backgroundColor: theme.palette.common.white,
    color: 'rgba(0, 0, 0, 0.87)',
    boxShadow: theme.shadows[3],
    fontSize: 11,
    maxWidth: '350px',
  },
  arrow: {
    color: theme.palette.common.white,
  },
}))(Tooltip);

const RateDetailDropdownPopover: React.FC<any> = ({
  numberOfNights, roomPrice, fontSize, alignClass, popoverAlignClass, dropdownLabel,
  tripId, doShowMarkupRates,
}) => {
  const rate: HotelRate = useSelector((state) => getHotelRate({ reduxState: state, tripId, rate: roomPrice }));
  const user: User = useSelector((state: RootState) => state.user);
  const taxesAndFeesToolTipText = user && user.travelAgency && user.travelAgency.settings && user.travelAgency.settings.taxesAndFeesToolTipText;
  const isTaxIncludedInRate = rate.averageNightlyRate === rate.averageNightlyRateBeforeTax;
  const markupPrice = rate.markupPrice > 0 ? +(rate.markupPrice - rate.originalPrice).toFixed(2) : 0;
  const doShowServiceFeesLine = doShowMarkupRates && taxesAndFeesToolTipText && markupPrice > 0;
  let averageNightlyRateBeforeTaxToDisplay = rate.averageNightlyRateBeforeTax;
  if (doShowServiceFeesLine) {
    averageNightlyRateBeforeTaxToDisplay = rate.originalAverageNightlyRateBeforeTax;
  }

  const infoTooltipElement = taxesAndFeesToolTipText && doShowMarkupRates &&
    (<LightTooltip title={taxesAndFeesToolTipText} placement='bottom' arrow>
      <div><RiInformationLine size={15} className='cursor-pointer' /></div>
    </LightTooltip>);

  return (rate
    && rate.averageNightlyRateBeforeTax > 0 ? (
    <InfoDropdownPopover
      dropdownTextPlaceholder={dropdownLabel}
      fontSize={fontSize}
      alignClass={alignClass}
      popoverAlignClass={popoverAlignClass}
    >
      <div className={`px-5 py-4 text-left font-normal ${fontSize || 'text-sm'}`}>
        <div className="font-bold">Price Details:</div>
        <hr className="my-2" />
        {/* <div className="font-bold">Nightly rates:</div>
          {roomPrice.nightlyRates.map((rate) => (
            <div className="grid grid-cols-2 ml-2">
              <div>
                For
                {' '}
                {dayjs(rate.startDate).format('M/D')}
                {' '}
                to
                {' '}
                {dayjs(rate.endDate).format('M/D')}
              </div>
              <div className="text-right">
                <NumberFormat
                  value={rate.amountBeforeTax || 0}
                  displayType="text"
                  thousandSeparator
                  prefix="$"
                  fixedDecimalScale="true"
                  decimalScale="2"
                />
              </div>
            </div>
          ))} */}
        <div className="grid grid-cols-2">
          <div>
            {numberOfNights}
            {numberOfNights > 1 ? ' Nights' : ' Night'}
          </div>
          <div className="text-right">
            <NumberFormat
              value={averageNightlyRateBeforeTaxToDisplay * numberOfNights}
              displayType="text"
              thousandSeparator
              prefix={currencySymbol(rate.currencyCode)}
              fixedDecimalScale
              decimalScale={2}
            />
            {numberOfNights > 1 ? (
              <div className="text-xs mb-1">
                <NumberFormat
                  value={averageNightlyRateBeforeTaxToDisplay || 0}
                  displayType="text"
                  thousandSeparator
                  prefix={currencySymbol(rate.currencyCode)}
                  fixedDecimalScale
                  decimalScale={2}
                />
                {' '}
                per night
              </div>
            ) : <></>}
          </div>
        </div>
        {rate.hotelTaxes && rate.hotelTaxes.length > 0 ? <hr className="my-2" /> : <></>}
        <div className="grid grid-cols-2 mt-2">
          <div className="flex gap-1 items-center">Total Tax</div>
          <div className="text-right">
            {rate.totalHotelTax && rate.totalHotelTax > 0 ? (
              <NumberFormat
                value={rate.totalHotelTax || 0}
                displayType="text"
                thousandSeparator
                prefix={currencySymbol(rate.currencyCode)}
                fixedDecimalScale
                decimalScale={2}
              />
            ) : (
              <></>
            )}
            {!rate.totalHotelTax && (
              <i>{isTaxIncludedInRate ? 'Included' : 'N/A'}</i>
            )}
          </div>
        </div>
        {rate.hotelTaxes && rate.hotelTaxes.length > 0 ? (
          rate.hotelTaxes.map((hotelTax, index) => (
            <div className="grid grid-cols-2 mt-2" key={index}>
              <div
                className="flex gap-1 items-center"
                style={{ paddingLeft: "20px" }}
              >
                {hotelTax.name}
              </div>
              <div className="text-right" style={{ paddingRight: "40px" }}>
                <NumberFormat
                  value={hotelTax.value}
                  displayType="text"
                  thousandSeparator
                  prefix={currencySymbol(rate.currencyCode)}
                  fixedDecimalScale
                  decimalScale={2}
                />
              </div>
            </div>
          ))
        ) : (
          <></>
        )}
        {rate.hotelFees && rate.hotelFees.length > 0 ? <hr className="my-2" /> : <></>}
        <div className="grid grid-cols-2 mt-2">
          <div className="flex gap-1 items-center">Total Fee</div>
          <div className="text-right">
            {rate.totalHotelFee && rate.totalHotelFee > 0 ? (
              <NumberFormat
                value={rate.totalHotelFee || 0}
                displayType="text"
                thousandSeparator
                prefix={currencySymbol(rate.currencyCode)}
                fixedDecimalScale
                decimalScale={2} />
            ) : (
              <></>
            )}
            {!rate.totalHotelFee && (
              <i>{isTaxIncludedInRate ? 'Included' : 'N/A'}</i>
            )}
          </div>
        </div>
        {rate.hotelFees && rate.hotelFees.length > 0 ? (
          rate.hotelFees.map((hotelFee, index) => (
            <div className="grid grid-cols-2 mt-2" key={index}>
              <div
                className="flex gap-1 items-center"
                style={{ paddingLeft: "20px" }}
              >
                {hotelFee.name}
              </div>
              <div className="text-right" style={{ paddingRight: "40px" }}>
                <NumberFormat
                  value={hotelFee.value}
                  displayType="text"
                  thousandSeparator
                  prefix={currencySymbol(rate.currencyCode)}
                  fixedDecimalScale
                  decimalScale={2} />
              </div>
            </div>
          ))
        ) : (
          <></>
        )}

        {doShowServiceFeesLine && (
          <>
            <hr className="my-2" />
            <div className="grid grid-cols-2 mt-2">
              <div className="flex gap-1 items-center">
                Service fee
                {infoTooltipElement}
              </div>
              <NumberFormat
                value={markupPrice}
                displayType="text"
                thousandSeparator
                prefix={currencySymbol(rate.currencyCode)}
                fixedDecimalScale
                decimalScale={2}
                className="text-right"
              />
            </div>
          </>
        )}
        <hr className="my-2" />
        <div className="grid grid-cols-2 font-bold">
          <div>
            Total Price
          </div>
          <div className="text-right">
            <NumberFormat
              value={rate.price}
              displayType="text"
              thousandSeparator
              prefix={currencySymbol(rate.currencyCode)}
              fixedDecimalScale
              decimalScale={2}
            />
          </div>
        </div>
        <DisplayCurrencyMessage roomPrices={[roomPrice]} className="text-gray-500 pt-4" />
      </div>
    </InfoDropdownPopover>
  ) : (
    <></>
  ));
};

RateDetailDropdownPopover.propTypes = {
  roomPrice: PropTypes.shape({}).isRequired,
  fontSize: PropTypes.string,
  alignClass: PropTypes.string,
  numberOfNights: PropTypes.number.isRequired,
  popoverAlignClass: PropTypes.string,
  dropdownLabel: PropTypes.string,
  tripId: PropTypes.string,
  doShowMarkupRates: PropTypes.bool,
};

RateDetailDropdownPopover.defaultProps = {
  fontSize: undefined,
  alignClass: 'justify-end',
  popoverAlignClass: undefined,
  dropdownLabel: 'Price Detail',
  tripId: undefined,
  doShowMarkupRates: false,
};

export default memo(RateDetailDropdownPopover);
